import { Storage } from '@aws-amplify/storage'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import UploadFileIcon from '@material-ui/icons/CloudUpload'
import React, { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { COLORS, FONTS } from '../../constants'
import { styles } from './style'

interface ChooseImageProps {
  images: any
  currentImage: string
  setSelectedImage: Function
}

const ChooseImage = ({
  images,
  currentImage,
  setSelectedImage,
}: ChooseImageProps) => {
  const classes = styles()

  const [processedImages, setProcessedImages] = useState<string[]>([])
  const [isProcessingImages, setIsProcessingImages] = useState(false)
  const [selectedPorcessedImage, setSelectedProcessedImage] = useState('')
  const [isUploadingImg, setIsUploadingImg] = useState(false)
  const [uploadedImage, setUploadedImage] = useState('')
  const [error, setError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)

  useEffect(() => {
    if (currentImage) {
      setSelectedImage(currentImage)
      setUploadedImage(currentImage)
    }
    return () => {
      setSelectedImage('')
      setUploadedImage('')
      setSelectedProcessedImage('')
      setIsProcessingImages(false)
      setIsUploadingImg(false)
      setError('')
      setProcessedImages([])
    }
  }, [currentImage])

  useEffect(() => {
    let isMounted = true
    setIsProcessingImages(true)
    setProcessedImages([])

    const loadImages = async () => {
      const loadPromises = images.map((imgSrc: string) => {
        return new Promise<string | null>(resolve => {
          const img = new Image()
          img.src = imgSrc
          img.onload = () => {
            if (img.width > 100 && img.height > 100) {
              resolve(imgSrc)
            } else {
              resolve(null)
            }
          }
          img.onerror = () => resolve(null)
        })
      })

      const results = await Promise.all(loadPromises)
      const validImages = results.filter(Boolean) as string[]

      if (isMounted) {
        setProcessedImages(validImages)
        setIsProcessingImages(false)
      }
    }

    loadImages()

    return () => {
      isMounted = false
    }
  }, [images])

  useEffect(() => {
    if (!isProcessingImages && currentImage) {
      const match = processedImages.find(img => img.includes(currentImage))
      if (match) {
        setSelectedProcessedImage(match)
      }
    }
  }, [isProcessingImages, processedImages, currentImage])

  const onCheckImage = (e: any, img: string) => {
    e.preventDefault()
    setSelectedProcessedImage(img)
    setSelectedImage(img)
  }

  const onUploadImage = async (event: any) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setIsUploadingImg(true)

    try {
      const image = files[0]

      const normalizedImageName = image.name
        .toLowerCase()
        .replace(/[^a-z0-9.]/g, '-') // Keep dot for extension
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')

      const extension = image.name
        .substring(image.name.lastIndexOf('.') + 1)
        .toLowerCase()

      const extensionToMime: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        svg: 'image/svg+xml',
        webp: 'image/webp',
        avif: 'image/avif',
      }

      const contentType =
        image.type || extensionToMime[extension] || 'image/jpeg'

      const imageName = `${uuidv4()}-${normalizedImageName}`

      const result = await Storage.put(imageName, image, {
        level: 'public',
        contentType,
      })

      const imageUrl = `https://${process.env.GATSBY_S3_BUCKET_NAME}.s3.us-east-2.amazonaws.com/public/${result.key}`

      setUploadedImage(imageUrl)
      setSelectedImage(imageUrl)
    } catch (err) {
      setError('something went wrong')
    } finally {
      setIsUploadingImg(false)
    }
  }

  return (
    <>
      <Typography variant="subtitle1">select from available</Typography>

      {isProcessingImages ? (
        <CircularProgress size={40} />
      ) : (
        <Box className={classes.imagesContainer}>
          {processedImages.length > 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: 5 }}
            >
              {processedImages.slice(0, 16).map(imgUrl => (
                <Box key={imgUrl} className={classes.imageWrapper}>
                  <img
                    src={imgUrl}
                    key={imgUrl}
                    className={classes.image}
                    alt="choose-image"
                  />

                  <div
                    className={classes.selectionOverlay}
                    style={{
                      background:
                        selectedPorcessedImage === imgUrl
                          ? COLORS.watermelonPink
                          : 'auto',
                    }}
                  />

                  <label
                    htmlFor={`choose-image-${imgUrl}`}
                    className={classes.imageLabel}
                  />

                  <input
                    type="checkbox"
                    id={`choose-image-${imgUrl}`}
                    checked={selectedPorcessedImage === imgUrl}
                    onChange={e => onCheckImage(e, imgUrl)}
                    style={{ display: 'none' }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              variant="subtitle1"
              component="p"
              className={classes.error}
            >
              not available for this product
            </Typography>
          )}
        </Box>
      )}

      <Typography
        variant="subtitle1"
        style={{ marginTop: '2rem', marginBottom: 0 }}
      >
        manual upload
      </Typography>

      {isUploadingImg ? (
        <CircularProgress />
      ) : (
        <Box
          mt={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          {uploadedImage && (
            <Box mb={2} className={classes.currentImgWrapper}>
              <img
                className={classes.image}
                src={uploadedImage}
                alt="current-image"
              />

              <IconButton
                size="small"
                className={classes.deleteButton}
                onClick={() => setConfirmOpen(true)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}

          <label
            className={classes.imgUpload}
            style={{
              cursor: 'pointer',
              fontFamily: FONTS.Graphik.GraphikLight,
            }}
          >
            <UploadFileIcon fontSize="large" />

            <Typography variant="subtitle1" style={{ margin: 0 }}>
              {uploadedImage ? 'update' : 'upload'} product image
            </Typography>

            <input
              type="file"
              accept="image/jpg, image/jpeg, image/png, image/gif, image/svg+xml, image/webp, image/avif"
              onChange={onUploadImage}
              style={{ visibility: 'hidden', height: 0 }}
            />
          </label>
        </Box>
      )}

      {error && (
        <Typography variant="subtitle1" component="p" className={classes.error}>
          {error}
        </Typography>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent>
          are you sure you want to delete this image?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmOpen(false)
              setSelectedImage(null)
              setUploadedImage('')
            }}
            style={{ color: COLORS.dangerRed }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ChooseImage
